import React, { useState, useCallback, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const INDICES = [
  { id: 'ICL', idBCRA: 40, nombre: 'ICL — Índice para Contratos de Locación', desc: 'Diseñado para contratos de alquiler (Ley 27.551). Combina inflación y salarios.' },
  { id: 'CER', idBCRA: 30, nombre: 'CER — Coeficiente de Estabilización de Referencia', desc: 'Ajusta valores según la inflación (IPC) de forma diaria.' },
  { id: 'UVA', idBCRA: 31, nombre: 'UVA — Unidad de Valor Adquisitivo', desc: 'Ajusta según el costo de construcción. Usado en créditos hipotecarios.' },
  { id: 'IPC', idBCRA: 27, nombre: 'IPC — Índice de Precios al Consumidor (inflación)', desc: 'Mide la variación mensual de precios al consumidor (INDEC). Se acumula mes a mes.' },
  { id: 'CasaPropia', fsDoc: 'casaPropia', nombre: 'Casa Propia — Coeficiente', desc: 'Coeficiente del Ministerio de Desarrollo Territorial. Ajuste trimestral (DNU 811/2023).', valorRelativo: true },
  { id: 'BolsaCemento', fsDoc: 'bolsaCemento', nombre: 'Bolsa de Cemento', desc: 'Ajuste según la variación del precio de la bolsa de cemento portland de 50 kg (o 2 de 25 kg).' },
];

const PERIODOS = [
  { meses: 1, label: '1 mes' },
  { meses: 2, label: '2 meses' },
  { meses: 3, label: '3 meses' },
  { meses: 4, label: '4 meses' },
  { meses: 6, label: '6 meses' },
  { meses: 12, label: '12 meses (anual)' },
];

const API_BASE = 'https://api.bcra.gob.ar/estadisticas/v4.0/Monetarias';

function fmtMoneda(n) {
  return n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(n) {
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
}

function fmtFecha(iso) {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function sumarMeses(fecha, meses) {
  const d = new Date(fecha);
  d.setMonth(d.getMonth() + meses);
  return d;
}

function toISO(d) {
  return d.toISOString().split('T')[0];
}

async function fetchDatosBCRA(idVariable) {
  const resp = await fetch(`${API_BASE}/${idVariable}`);
  if (!resp.ok) throw new Error(`Error ${resp.status} al consultar el BCRA`);
  const json = await resp.json();
  const items = json.results?.[0]?.detalle;
  if (!items?.length) throw new Error('Sin datos del BCRA para este índice');
  const fechas = items.map(d => d.fecha).sort();
  return { datos: items, ultimaFecha: fechas[fechas.length - 1] };
}

async function fetchIPCIndec() {
  // datos.gob.ar provee el nivel del IPC base Dic 2016 con 4 decimales.
  // La variación mensual calculada desde el nivel tiene precisión completa (vs 1 decimal en el BCRA).
  const resp = await fetch('https://apis.datos.gob.ar/series/api/series/?ids=148.3_INIVELNAL_DICI_M_26&limit=300&format=json');
  if (!resp.ok) throw new Error(`Error ${resp.status} al consultar los datos del INDEC`);
  const json = await resp.json();
  const raw = json.data;
  if (!raw?.length) throw new Error('Sin datos del INDEC');
  raw.sort((a, b) => a[0].localeCompare(b[0]));
  const items = [];
  for (let i = 1; i < raw.length; i++) {
    const prev = raw[i - 1][1];
    const curr = raw[i][1];
    if (prev && curr) {
      items.push({ fecha: raw[i][0].substring(0, 10), valor: (curr / prev - 1) * 100 });
    }
  }
  if (!items.length) throw new Error('Sin datos del INDEC');
  const fechas = items.map(d => d.fecha).sort();
  return { datos: items, ultimaFecha: fechas[fechas.length - 1] };
}

async function fetchIndicadorFirestore(fsDocId) {
  let snap;
  try {
    snap = await getDoc(doc(db, 'indicadores', fsDocId));
  } catch (e) {
    throw new Error('Este índice aún no tiene datos cargados. Los datos se actualizan diariamente.');
  }
  if (!snap.exists()) throw new Error('Este índice aún no tiene datos cargados. Los datos se actualizan diariamente.');
  const data = snap.data();
  if (!data.valores?.length) throw new Error('Este índice aún no tiene valores cargados.');
  const items = data.valores
    .filter(v => v.fecha && v.valor != null)
    .map(v => ({ fecha: v.fecha + '-01', valor: v.valor }))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
  if (!items.length) throw new Error('Sin datos para este índice.');
  return { datos: items, ultimaFecha: items[items.length - 1].fecha };
}

function buscarPrimerDiaMes(datos, fechaTarget) {
  const year = fechaTarget.getFullYear();
  const month = fechaTarget.getMonth();
  const delMes = datos.filter(d => {
    const fd = new Date(d.fecha + 'T12:00:00');
    return fd.getFullYear() === year && fd.getMonth() === month;
  });
  if (!delMes.length) return null;
  delMes.sort((a, b) => a.fecha.localeCompare(b.fecha));
  return delMes[0];
}

function fmtMesCorto(iso) {
  const d = new Date(iso + 'T12:00:00');
  const m = d.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '');
  return `${m} ${d.getFullYear()}`;
}

function mesYAnio(fecha) {
  return fecha.getFullYear() * 12 + fecha.getMonth();
}

function calcularAjustesIPC(datos, fechaInicio, periodoMeses, totalMeses) {
  const sorted = [...datos].sort((a, b) => a.fecha.localeCompare(b.fecha));
  const mesBase = mesYAnio(fechaInicio);
  const ajustes = [];
  let valorActual = 1;
  let yaDesplazo = false;

  for (let i = periodoMeses; i <= totalMeses; i += periodoMeses) {
    if (yaDesplazo) break;

    const mesesEsperados = [];
    for (let m = i - periodoMeses; m < i; m++) {
      const abs = mesBase + m;
      mesesEsperados.push(`${Math.floor(abs / 12)}-${String((abs % 12) + 1).padStart(2, '0')}`);
    }

    let mesesUsados = sorted.filter(d => mesesEsperados.includes(d.fecha.substring(0, 7)));

    let desplazado = false;
    if (mesesUsados.length === 0) break;
    if (mesesUsados.length < periodoMeses) {
      mesesUsados = sorted.slice(-periodoMeses);
      if (mesesUsados.length === 0) break;
      desplazado = true;
      yaDesplazo = true;
    }

    let acumulado = 1;
    for (const m of mesesUsados) {
      acumulado *= (1 + m.valor / 100);
    }

    const mesAjuste = mesBase + i;
    const fechaAjuste = new Date(Math.floor(mesAjuste / 12), mesAjuste % 12, 1);

    valorActual *= acumulado;
    ajustes.push({
      periodo: i,
      fecha: toISO(fechaAjuste),
      factor: valorActual,
      pctAcumulado: (valorActual - 1) * 100,
      pctPeriodo: (acumulado - 1) * 100,
      mesesConDato: mesesUsados.length,
      mesesEsperados: periodoMeses,
      rangoDesde: mesesUsados[0].fecha,
      rangoHasta: mesesUsados[mesesUsados.length - 1].fecha,
      desplazado,
    });
  }
  return ajustes;
}

function calcularAjustesIndice(datos, fechaInicio, periodoMeses, totalMeses) {
  const valorInicio = buscarPrimerDiaMes(datos, fechaInicio);
  if (!valorInicio) return [];

  const ajustes = [];
  for (let i = periodoMeses; i <= totalMeses; i += periodoMeses) {
    const fechaAjuste = sumarMeses(fechaInicio, i);
    const valorAjuste = buscarPrimerDiaMes(datos, fechaAjuste);
    if (!valorAjuste) break;

    const factor = valorAjuste.valor / valorInicio.valor;
    ajustes.push({
      periodo: i,
      fecha: valorAjuste.fecha,
      fechaIndiceInicio: valorInicio.fecha,
      valorIndiceInicio: valorInicio.valor,
      fechaIndiceActual: valorAjuste.fecha,
      valorIndiceActual: valorAjuste.valor,
      factor,
      pctAcumulado: (factor - 1) * 100,
    });
  }
  return ajustes;
}

export default function CalculadoraAlquileres() {
  const [alquiler, setAlquiler] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [periodo, setPeriodo] = useState(12);
  const [indiceId, setIndiceId] = useState('ICL');
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [indicadoresActivos, setIndicadoresActivos] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'config', 'procesos')).then(snap => {
      if (snap.exists() && snap.data().indicadoresActivo) setIndicadoresActivos(true);
    }).catch(() => {});
  }, []);

  const calcular = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setResultado(null);

    const indice = INDICES.find(i => i.id === indiceId);
    if (indice.fsDoc && !indicadoresActivos) {
      setError('Este índice no está disponible aún. Próximamente.');
      return;
    }

    const monto = parseFloat(alquiler);
    if (!monto || monto <= 0) { setError('Ingresá un valor de alquiler válido.'); return; }
    if (!fechaInicio) { setError('Seleccioná la fecha de inicio del contrato.'); return; }

    setCargando(true);

    try {
      let fetchResult;
      if (indice.fsDoc) {
        fetchResult = await fetchIndicadorFirestore(indice.fsDoc);
      } else if (indiceId === 'IPC') {
        fetchResult = await fetchIPCIndec();
      } else {
        fetchResult = await fetchDatosBCRA(indice.idBCRA);
      }
      const { datos, ultimaFecha } = fetchResult;

      const fi = new Date(fechaInicio + '-01T12:00:00');
      const hoy = new Date();
      const mesesTranscurridos = (hoy.getFullYear() - fi.getFullYear()) * 12 + (hoy.getMonth() - fi.getMonth());
      const totalMeses = Math.max(periodo, Math.min(mesesTranscurridos + periodo, 120));

      let ajustes;
      if (indiceId === 'IPC') {
        ajustes = calcularAjustesIPC(datos, fi, periodo, totalMeses);
      } else {
        ajustes = calcularAjustesIndice(datos, fi, periodo, totalMeses);
      }

      if (!ajustes.length) {
        setError('No hay datos suficientes para el rango de fechas seleccionado.');
        setCargando(false);
        return;
      }

      const ultimoAjuste = ajustes[ajustes.length - 1];
      setResultado({
        montoOriginal: monto,
        indice: indice.id,
        indiceNombre: indice.nombre,
        valorRelativo: !!indice.valorRelativo,
        periodo,
        ultimaFecha,
        ajustes: ajustes.map(a => ({
          ...a,
          montoAjustado: monto * a.factor,
        })),
        montoActual: monto * ultimoAjuste.factor,
        pctTotal: ultimoAjuste.pctAcumulado,
      });
    } catch (err) {
      setError(err.message || 'Error al consultar la API del BCRA. Intentá nuevamente.');
    }
    setCargando(false);
  }, [alquiler, fechaInicio, periodo, indiceId, indicadoresActivos]);

  const indiceActual = INDICES.find(i => i.id === indiceId);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '30px 20px 60px' }}>
      <h1 style={{ color: '#1e3a6e', fontSize: '28px', margin: '0 0 6px', fontWeight: '800' }}>
        Calculadora de ajuste de alquileres
      </h1>
      <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 20px', lineHeight: 1.5 }}>
        Los datos se consultan en tiempo real desde la API del BCRA, INDEC y fuentes oficiales.
      </p>
      <div>

        <form onSubmit={calcular} style={{ background: 'white', borderRadius: '12px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Valor actual del alquiler ($)</label>
              <input
                type="number"
                min="1"
                step="0.01"
                value={alquiler}
                onChange={e => setAlquiler(e.target.value)}
                placeholder="Ej: 250000"
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelStyle}>Mes de inicio del contrato</label>
              <input
                type="month"
                value={fechaInicio}
                onChange={e => setFechaInicio(e.target.value)}
                max={new Date().toISOString().substring(0, 7)}
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelStyle}>Período de ajuste</label>
              <select value={periodo} onChange={e => setPeriodo(Number(e.target.value))} style={inputStyle}>
                {PERIODOS.map(p => <option key={p.meses} value={p.meses}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <label style={labelStyle}>Índice de actualización</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            {INDICES.map(idx => (
              <label
                key={idx.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '14px', borderRadius: '8px',
                  cursor: idx.fsDoc && !indicadoresActivos ? 'not-allowed' : 'pointer',
                  border: indiceId === idx.id && !(idx.fsDoc && !indicadoresActivos) ? '2px solid #1e3a6e' : '2px solid #e2e8f0',
                  background: idx.fsDoc && !indicadoresActivos ? '#f1f5f9' : indiceId === idx.id ? '#f0f5ff' : '#fafbfc',
                  opacity: idx.fsDoc && !indicadoresActivos ? 0.6 : 1,
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="radio"
                  name="indice"
                  value={idx.id}
                  checked={indiceId === idx.id}
                  onChange={() => !(idx.fsDoc && !indicadoresActivos) && setIndiceId(idx.id)}
                  disabled={!!(idx.fsDoc && !indicadoresActivos)}
                  style={{ marginTop: '3px', accentColor: '#1e3a6e' }}
                />
                <div>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: idx.fsDoc && !indicadoresActivos ? '#94a3b8' : '#1e3a6e' }}>
                    {idx.id === 'CasaPropia' ? 'Casa Propia' : idx.id === 'BolsaCemento' ? 'Bolsa de Cemento' : idx.id}
                    {idx.fsDoc && !indicadoresActivos && <span style={{ fontSize: '11px', fontWeight: '500', color: '#94a3b8', marginLeft: '8px' }}>Próximamente</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{idx.desc}</div>
                </div>
              </label>
            ))}
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', color: '#991b1b', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={cargando} style={{
            background: cargando ? '#94a3b8' : '#1e3a6e',
            color: 'white', border: 'none', borderRadius: '8px',
            padding: '14px 36px', fontSize: '16px', fontWeight: '700',
            cursor: cargando ? 'wait' : 'pointer',
            transition: 'background 0.15s',
            width: '100%',
          }}>
            {cargando ? 'Consultando datos…' : 'Calcular ajuste'}
          </button>
        </form>

        {/* Resultado */}
        {resultado && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '28px' }}>
              <div style={tarjetaStyle}>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Alquiler original</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#1e3a6e' }}>{fmtMoneda(resultado.montoOriginal)}</div>
              </div>
              <div style={{ ...tarjetaStyle, borderColor: '#16a34a' }}>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Valor ajustado actual</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#16a34a' }}>{fmtMoneda(resultado.montoActual)}</div>
              </div>
              <div style={tarjetaStyle}>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Aumento acumulado</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: resultado.pctTotal >= 0 ? '#dc2626' : '#16a34a' }}>{fmtPct(resultado.pctTotal)}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <h3 style={{ color: '#1e3a6e', fontSize: '16px', margin: 0, fontWeight: '700' }}>
                Detalle de ajustes por {indiceActual.id}
              </h3>
              <span style={{ fontSize: '12px', color: '#64748b', background: '#f1f5f9', padding: '3px 10px', borderRadius: '12px' }}>
                Datos al {fmtMesCorto(resultado.ultimaFecha)}
              </span>
            </div>
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 16px' }}>
              Cada {periodo === 1 ? 'mes' : `${periodo} meses`} · Índice: {indiceActual.nombre.split('—')[0].trim()}
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={thStyle}>Ajuste</th>
                    <th style={thStyle}>Fecha</th>
                    {resultado.indice !== 'IPC' && !resultado.valorRelativo && <th style={thStyle}>Valor índice</th>}
                    <th style={thStyle}>Aumento período</th>
                    <th style={thStyle}>Aumento acum.</th>
                    <th style={thStyle}>Nuevo alquiler</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.ajustes.map((a, i) => {
                    const pctPeriodo = i === 0
                      ? a.pctAcumulado
                      : ((a.factor / resultado.ajustes[i - 1].factor) - 1) * 100;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: a.desplazado ? '#fffbeb' : undefined }}>
                        <td style={tdStyle}>{`#${i + 1}`}</td>
                        <td style={tdStyle}>
                          {fmtMesCorto(a.fecha)}
                          {resultado.indice === 'IPC' && a.rangoDesde && (
                            <div style={{ fontSize: '11px', color: a.desplazado ? '#b45309' : '#64748b', marginTop: '2px' }}>
                              {a.desplazado ? '⚠ ' : ''}Inflación {fmtMesCorto(a.rangoDesde)} a {fmtMesCorto(a.rangoHasta)}
                            </div>
                          )}
                        </td>
                        {resultado.indice !== 'IPC' && !resultado.valorRelativo && (
                          <td style={tdStyle}>
                            <div>{a.valorIndiceActual?.toFixed(4)}</div>
                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                              {fmtFecha(a.fechaIndiceActual)}
                            </div>
                          </td>
                        )}
                        <td style={{ ...tdStyle, color: pctPeriodo >= 0 ? '#dc2626' : '#16a34a', fontWeight: '600' }}>
                          {fmtPct(pctPeriodo)}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: '600' }}>
                          {fmtPct(a.pctAcumulado)}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: '700', color: '#1e3a6e' }}>
                          {fmtMoneda(a.montoAjustado)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {resultado.ajustes.some(a => a.desplazado) && (
              <p style={{ fontSize: '12px', color: '#b45309', margin: '12px 0 0', lineHeight: 1.5 }}>
                ⚠ El IPC se publica alrededor del 15 de cada mes. Para el último período, aún no están publicados todos los meses, por lo que se utilizaron las últimas {resultado.periodo} inflaciones mensuales disponibles. No se omiten ni infieren valores: el cálculo usa exclusivamente datos publicados por INDEC.
              </p>
            )}

            <div style={{ marginTop: '24px', background: '#f8fafc', borderRadius: '8px', padding: '16px 20px', border: '1px solid #e2e8f0' }}>
              <p style={{ fontWeight: '700', fontSize: '14px', color: '#1e3a6e', margin: '0 0 8px' }}>¿Cómo se calcula?</p>
              {resultado.indice === 'IPC' ? (
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                  Para el IPC, se acumulan las variaciones mensuales del período:
                  <br /><b>Nuevo valor = Valor anterior × (1 + inflación₁/100) × (1 + inflación₂/100) × …</b>
                  <br />Esto es distinto a sumar los porcentajes (la inflación se compone).
                  <br /><span style={{ fontSize: '12px', color: '#94a3b8' }}>Los datos provienen del nivel del IPC (base Dic 2016) publicado por INDEC. La variación mensual se calcula desde el nivel, con precisión completa.</span>
                </p>
              ) : resultado.indice === 'BolsaCemento' ? (
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                  <b>Nuevo valor = Alquiler original × (Precio actual ÷ Precio al inicio)</b>
                  <br />Se toma el costo de la bolsa de cemento portland de 50 kg (o 2 de 25 kg si no se comercializa) de primera línea en Formosa Capital.
                  <br />Para determinar el precio se promedia entre tres casas comerciales: <b>Mateo Construcciones</b>, <b>Nasser Construcciones</b> y <b>Familia Bercomat S.A.</b>
                </p>
              ) : indiceActual.fsDoc ? (
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                  <b>Nuevo valor = Alquiler original × (Valor actual ÷ Valor al inicio)</b>
                  <br />Se toma el valor mensual publicado de cada período. Se compara contra el valor del mes de inicio del contrato.
                </p>
              ) : (
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                  <b>Nuevo valor = Alquiler original × (Índice actual ÷ Índice al inicio)</b>
                  <br />Se toma el valor del índice del primer día hábil de cada mes. Se compara contra el valor al inicio del contrato.
                </p>
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: '28px', background: '#fffbeb', borderRadius: '8px', padding: '16px 20px', border: '1px solid #fde68a' }}>
          <p style={{ fontWeight: '700', fontSize: '14px', color: '#92400e', margin: '0 0 6px' }}>Información importante</p>
          <ul style={{ fontSize: '13px', color: '#78716c', margin: 0, paddingLeft: '18px', lineHeight: 1.7 }}>
            <li>Los datos provienen de la <b>API del BCRA</b>, <b>INDEC</b> y fuentes oficiales, y se consultan en tiempo real.</li>
            <li>Esta calculadora es orientativa. El ajuste real depende de lo pactado en tu contrato.</li>
            <li>Las reglas de ajuste obligatorio (ICL, Casa Propia) aplican solo a contratos con <b>destino habitacional</b>. Los contratos comerciales siempre se pactaron libremente.</li>
            <li>Para contratos habitacionales entre el 1/7/2021 y el 18/10/2023: ajuste anual por ICL (Ley 27.551).</li>
            <li>Para contratos habitacionales entre el 18/10/2023 y el 29/12/2023: ajuste trimestral por índice Casa Propia (DNU 811/2023).</li>
            <li>Para contratos posteriores al 29/12/2023 (DNU 70/2023): índice y período se pactan libremente, tanto para vivienda como comercio.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontWeight: '600', fontSize: '13px', color: '#475569', marginBottom: '6px' };
const inputStyle = {
  width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1',
  borderRadius: '8px', fontSize: '15px', outline: 'none',
  transition: 'border-color 0.15s', boxSizing: 'border-box',
};
const tarjetaStyle = {
  padding: '18px 20px', borderRadius: '10px',
  border: '2px solid #e2e8f0', background: '#fafbfc',
};
const thStyle = { textAlign: 'left', padding: '10px 12px', fontWeight: '700', color: '#475569', fontSize: '13px' };
const tdStyle = { padding: '10px 12px', color: '#334155' };
